<script setup lang="ts">
  import { computed } from 'vue';
  import Teleport from 'vue2-teleport';
  import HeaderNav from './HeaderNav.vue';
  import { type NavLink } from '../config/navLinks';
  import { HeaderAccount, type AccountUser } from '@/features/headerAccount';
  import { useViewport, Breakpoint } from '@/shared/composables';
  import SkolkovoLogo from '@/shared/ui/logos/SkolkovoLogo.vue';
  import TravelataLogo from '@/shared/ui/logos/TravelataLogo.vue';

  interface SiteHeaderProps {
    user?: AccountUser | null;
    accountUrl?: string;
    visibleLogos?: boolean;
    shouldAdaptive?: boolean;
    buttonBordered?: boolean;
    primary?: NavLink[];
    more?: NavLink[];
  }

  const props = withDefaults(defineProps<SiteHeaderProps>(), {
    user: null,
    accountUrl: '',
    visibleLogos: true,
    shouldAdaptive: true,
    buttonBordered: true,
    primary: undefined,
    more: undefined,
  });

  interface SiteHeaderEmits {
    (e: 'login'): void;
    (e: 'logout'): void;
  }

  const emit = defineEmits<SiteHeaderEmits>();

  const { isSmaller } = useViewport();

  const isBreakpointActive = isSmaller(Breakpoint.L);

  const isAdaptive = computed(() => isBreakpointActive.value && props.shouldAdaptive);

  const onLogin = (): void => {
    emit('login');
  };

  const onLogout = (): void => {
    emit('logout');
  };
</script>

<template>
  <header
    :class="[
      'site-header',
      {
        'site-header_adaptive': shouldAdaptive,
      },
    ]"
    data-testid="site-header"
  >
    <div class="site-header__bar">
      <div class="site-header__left">
        <TravelataLogo v-if="visibleLogos" />
        <div
          v-if="!isAdaptive"
          class="site-header__left-nav-teleport"
        ></div>
        <Teleport :to="isAdaptive ? '.site-header__bottom-nav-teleport' : '.site-header__left-nav-teleport'">
          <slot name="nav"></slot>
          <HeaderNav
            v-if="!$slots.nav"
            :primary="primary"
            :more="more"
          />
        </Teleport>
      </div>

      <div class="site-header__right">
        <SkolkovoLogo v-if="visibleLogos" />
        <div class="site-header__action">
          <slot name="action"></slot>
          <HeaderAccount
            :user="user"
            :account-url="accountUrl"
            :bordered="buttonBordered"
            @login="onLogin"
            @logout="onLogout"
          />
        </div>
      </div>
      <div
        v-if="isAdaptive"
        class="site-header__bottom"
      >
        <div class="site-header__bottom-nav-teleport"></div>
        <div class="site-header__bottom-buy-teleport"></div>
      </div>
    </div>
  </header>
</template>

<style scoped lang="scss">
  .site-header {
    $root: &;

    background: linear-gradient(
      262deg,
      var(--color-cyan-40, #2ce) -14.86%,
      var(--color-brand-secondary, #218bff) 6.05%,
      var(--color-sky-50, #218bff) 112.65%
    );
    box-sizing: border-box;
    display: flex;
    justify-content: center;
    padding: var(--spacing-s) var(--spacing-l) 0 var(--spacing-l);
    width: 100%;

    &__action {
      align-items: center;
      display: flex;
      gap: var(--spacing-s);
      justify-content: center;
    }

    &__bar {
      align-items: center;
      display: flex;
      gap: var(--spacing-l);
      justify-content: space-between;
      margin: 0 auto;
      max-width: $max-width-xl;
      width: 100%;
    }

    &__left {
      align-items: center;
      display: flex;
      gap: var(--spacing-xl);
    }

    &__right {
      align-items: center;
      display: flex;
      gap: var(--spacing-xxl);
    }

    &_adaptive {
      @media (max-width: $breakpoint-l) {
        #{$root}__bar {
          align-items: center;
          display: grid;
          gap: var(--spacing-m) var(--spacing-l);
          grid-template-columns: 1fr auto;
          grid-template-rows: auto auto;
        }

        #{$root}__left {
          grid-column: 1 / 2;
          grid-row: 1 / 2;
        }
        #{$root}__right {
          grid-column: 2 / 3;
          grid-row: 1 / 2;
          justify-self: end;
        }
        #{$root}__bottom {
          align-items: center;
          display: flex;
          grid-column: 1 / -1;
          grid-row: 2 / 3;
          justify-content: space-between;
        }
      }
    }
    @include responsive-max-width('&__bar');
  }
</style>
